import * as THREE from 'three';
import type { PergolaConfig } from '../config/types';
import { buildPergola } from './buildPergola';

/**
 * Envuelve el grupo 3D de la pérgola y gestiona su ciclo de vida:
 * reconstrucción ante cambios de configuración, animación de apertura de lamas
 * y liberación de recursos (geometrías/materiales).
 */
export class Pergola {
  readonly group = new THREE.Group();

  private config: PergolaConfig;
  private lamas: THREE.Mesh[] = [];
  private aluMaterial: THREE.MeshStandardMaterial | null = null;
  private lamaMaterial: THREE.MeshStandardMaterial | null = null;
  private lamaCurrentAngle = 0;
  private lamaTargetAngle = 0;
  totalHeight = 0;

  constructor(config: PergolaConfig) {
    this.config = { ...config };
    this.group.name = 'pergola-root';
    this.rebuild(this.config);
  }

  /** Reconstruye toda la geometría a partir de una nueva configuración. */
  rebuild(config: PergolaConfig): void {
    this.config = { ...config };
    this.clear();

    const parts = buildPergola(this.config);
    this.group.add(parts.group);
    this.lamas = parts.lamas;
    this.aluMaterial = parts.aluMaterial;
    this.lamaMaterial = parts.lamaMaterial;
    this.totalHeight = parts.totalHeight;

    this.lamaCurrentAngle = config.lamasOpen ? (config.lamasDir * Math.PI) / 2 : 0;
    this.lamaTargetAngle = this.lamaCurrentAngle;
  }

  /** Aplica un color (hex sRGB) a la estructura sin reconstruir la geometría. */
  setColorHex(colorHex: string): void {
    this.config.colorHex = colorHex;
    this.aluMaterial?.color.set(colorHex);
  }

  /** Aplica un color (hex sRGB) a las lamas sin reconstruir. */
  setLamasColorHex(colorHex: string): void {
    this.config.lamasColorHex = colorHex;
    this.lamaMaterial?.color.set(colorHex);
  }

  /** Abre/cierra las lamas (animado en cada frame vía update). */
  setLamasOpen(open: boolean): void {
    this.config.lamasOpen = open;
    this.lamaTargetAngle = open ? (this.config.lamasDir * Math.PI) / 2 : 0;
  }

  toggleLamas(): boolean {
    const next = !this.config.lamasOpen;
    this.setLamasOpen(next);
    return next;
  }

  get lamasOpen(): boolean {
    return this.config.lamasOpen;
  }

  /** Llamado en cada frame con el delta de tiempo (s): anima la apertura de lamas. */
  update(dt: number): void {
    if (Math.abs(this.lamaCurrentAngle - this.lamaTargetAngle) < 1e-4) return;
    const speed = 3.5; // rad/s aprox.
    const step = Math.sign(this.lamaTargetAngle - this.lamaCurrentAngle) * speed * dt;
    if (Math.abs(step) >= Math.abs(this.lamaTargetAngle - this.lamaCurrentAngle)) {
      this.lamaCurrentAngle = this.lamaTargetAngle;
    } else {
      this.lamaCurrentAngle += step;
    }
    for (const lama of this.lamas) lama.rotation.x = this.lamaCurrentAngle;
  }

  private clear(): void {
    for (let i = this.group.children.length - 1; i >= 0; i--) {
      const child = this.group.children[i];
      this.group.remove(child);
      disposeObject(child);
    }
    this.lamas = [];
  }

  dispose(): void {
    this.clear();
  }
}

/** Libera geometrías y materiales de un objeto y sus descendientes. */
function disposeObject(obj: THREE.Object3D): void {
  obj.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    if (mesh.material) {
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const m of mats) m.dispose();
    }
  });
}
